import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  buildChartSeries,
  buildEnergyExpenditureStats,
  buildGoalData,
  buildGoalProjectionSeries,
  buildInsights,
  buildMacroBreakdownSeries,
  buildPhaseSpans,
  buildSummaryStats,
  toUtcNumber,
} from "../../../../../utils/nutrition-progress";
import { BodyweightGoal } from "./BodyweightGoal";
import { BodyweightTrendCard } from "./BodyweightTrendCard";
import { InsightsPanel } from "./InsightsPanel";
import { MetricCards } from "./MetricCards";
import { PlannerPromo } from "./PlannerPromo";
import { SummaryDisclaimer } from "./SummaryDisclaimer";
import { SummaryHero } from "./SummaryHero";

// Summary view — default landing tab.
// S1: metric cards. S2: Hero chart + TDEE card. S3: Insights. S4: PlannerPromo + Bodyweight goal.

/* ---- main view ------------------------------------------------------- */

const SummaryView = ({
  dayRows = [],
  tdeeDayRows = [],
  dailyStats = [],
  athlete,
  dateFrom,
  dateTo,
  units = "metric",
  bodyweightGoal = null,
  tdeeOverride = null,
  onTdeeOverrideSaved,
  onTdeeOverrideReset,
  onNavigateToReports,
}) => {
  const { t, i18n } = useTranslation("athlete-portal");

  const fromUtc = useMemo(() => toUtcNumber(dateFrom), [dateFrom]);
  const toUtc = useMemo(() => toUtcNumber(dateTo), [dateTo]);
  // Shared with goalData below (was previously computed twice, once for
  // Logging Rate's own cap and once for the goal projection's weeksToTarget).
  const todayUtc = useMemo(() => toUtcNumber(new Date()), []);

  const s = useMemo(
    () => buildSummaryStats(dayRows, todayUtc),
    [dayRows, todayUtc]
  );
  // tdeeDayRows (always trailing 3 months, NOT dayRows) + tdeeOverride both
  // live one level up (NutritionProgress.js) so ReportsView.js's own
  // intake-vs-bodyweight chart reads the same resolved values — see that
  // component's own comments for the full "why".
  const expenditure = useMemo(
    () => buildEnergyExpenditureStats(tdeeDayRows, tdeeOverride),
    [tdeeDayRows, tdeeOverride]
  );
  const series = useMemo(() => buildChartSeries(dayRows), [dayRows]);
  // Feeds Summary's chart-switcher "Stacked macros to target (include TDEE)"
  // option (SummaryHero.js) — same builder + same "Avg" label ReportsView.js
  // already uses for its own macro-breakdown chart, so the two never drift.
  const macroSeries = useMemo(
    () => buildMacroBreakdownSeries(dayRows, t("nutritionPage.chart.avg")),
    [dayRows, t]
  );

  // Training-track spans stay their own value - buildInsights (below) reads
  // them, and its rules are training-phase specific.
  const phaseSpans = useMemo(
    () => buildPhaseSpans(athlete, fromUtc, toUtc),
    [athlete, fromUtc, toUtc]
  );

  // Chart overlay: both tracks (training + nutrition) shaped for the hero
  // chart. `track` keeps them distinct (unique React key, and a hook for
  // styling them differently later). Nutrition spans are only ever consumed
  // here, so they're built inline rather than as their own memo.
  const phaseAreas = useMemo(() => {
    const utcToIdx = new Map(series.map((d) => [d.utcDate, d.idx]));
    const toArea = (track) => (span) => ({
      x1: utcToIdx.get(span.startUtc) ?? 0,
      x2: utcToIdx.get(span.endUtc) ?? series.length - 1,
      color: span.color,
      name: span.name,
      isOngoing: span.isOngoing,
      track,
    });
    return [
      ...phaseSpans.map(toArea("training")),
      ...buildPhaseSpans(athlete, fromUtc, toUtc, "nutritionPhases").map(
        toArea("nutrition")
      ),
    ];
  }, [phaseSpans, athlete, fromUtc, toUtc, series]);

  // Goal projection — computed from bodyweightGoal + recent weight slope.
  // GoalAdjustModal.js persists via a real submit-form navigation (same as
  // GoalEmptyForm.js's create flow), so bodyweightGoal itself is always the
  // fresh, post-save SSR value — no local override state needed here.
  // Extracted to buildGoalData (nutrition-progress/analysis.js) so any other
  // consumer of a bodyweightGoal + dayRows pair (e.g. Periodization's
  // PhaseSummary) computes the identical trend/projection.
  const goalData = useMemo(
    () => buildGoalData(bodyweightGoal, dailyStats, todayUtc),
    [bodyweightGoal, dailyStats, todayUtc]
  );

  // Build the goal projection series for the nutrition page.
  const goalSeries = useMemo(
    () => buildGoalProjectionSeries(dayRows, goalData),
    [dayRows, goalData]
  );
  // Build the insights for the nutrition page.
  const isImperial = units === "imperial";
  const insights = useMemo(
    () =>
      buildInsights({
        dayRows,
        stats: s,
        phaseSpans,
        goalData,
        t,
        locale: i18n.language,
        isImperial,
      }),
    [dayRows, s, phaseSpans, goalData, t, i18n.language, isImperial]
  );

  return (
    <div>
      <InsightsPanel insights={insights} />

      {/* Stat tiles live right under Insights (the numbers those insights talk
          about); the disclaimer stays attached to them as their footnote. */}
      <MetricCards stats={s} units={units} />

      <SummaryDisclaimer />

      <SummaryHero
        series={series}
        macroSeries={macroSeries}
        phaseAreas={phaseAreas}
        expenditure={expenditure}
        units={units}
        athleteId={athlete?._id}
        athlete={athlete}
        dailyStats={dailyStats}
        onTdeeOverrideSaved={onTdeeOverrideSaved}
        onTdeeOverrideReset={onTdeeOverrideReset}
        onNavigateToReports={onNavigateToReports}
      />

      {/* Own quick-range control, independent of the reporting period above —
          see BodyweightTrendCard.js's own header comment. dailyStats here is
          the same RAW (unfiltered) list SummaryHero.js already uses for its
          latest-weight lookup, not dayRows/series (scoped to dateFrom/dateTo). */}
      <BodyweightTrendCard
        dailyStats={dailyStats}
        units={units}
        bodyweightGoal={bodyweightGoal}
      />

      {/* phaseAreas is empty only when neither track has a phase. */}
      {phaseAreas.length === 0 && <PlannerPromo athlete={athlete} />}


      <BodyweightGoal
        goalData={goalData}
        goalSeries={goalSeries}
        athlete={athlete}
        units={units}
      />
    </div>
  );
};

SummaryView.propTypes = {
  dayRows: PropTypes.array,
  tdeeDayRows: PropTypes.array,
  dailyStats: PropTypes.array,
  athlete: PropTypes.object,
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
  units: PropTypes.string,
  bodyweightGoal: PropTypes.object,
  tdeeOverride: PropTypes.object,
  onTdeeOverrideSaved: PropTypes.func,
  onTdeeOverrideReset: PropTypes.func,
  onNavigateToReports: PropTypes.func,
};

export default SummaryView;
